import React, { useEffect, useState } from 'react';
import { Alert, Box, IconButton, TextField, Rating, Stack, Tab, Tabs, alpha, styled, InputAdornment } from '@mui/material';
import { Square, StarBorder } from '@mui/icons-material';
import { DataGrid, gridClasses, Toolbar, useGridApiRef, GridToolbarColumnsButton } from '@mui/x-data-grid';
import ViewColumnRoundedIcon from '@mui/icons-material/ViewColumnRounded';
import { getReleaseDetailsById } from '../../Services';
import moment from 'moment';
import EventDetails from '../Common/EventDetails/EventDetails';
import "./ListView.css";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
        '&:hover, &.Mui-hovered': {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : alpha(theme.palette.grey[100]),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                .2 + theme.palette.action.selectedOpacity,
            ),
        },
    },
    [`& .${gridClasses.row}.odd`]: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : '#fff',
        '&:hover, &.Mui-hovered': {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[100],
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                .2 + theme.palette.action.selectedOpacity,
            ),
        },
    },
}));

export const StyledRating = styled(Rating)({
    '& .MuiRating-iconFilled': {
        color: '#333',
    },
    '& .MuiRating-iconHover': {
        color: '#333',
    },
});

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function QuickSearchToolbar({ apiRef }) {
  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);

    if (apiRef.current?.setQuickFilterValues) {
      apiRef.current.setQuickFilterValues(value ? [value] : []);
    } else {
      const fm = apiRef.current?.getFilterModel?.() ?? {};
      apiRef.current?.setFilterModel?.({
        ...fm,
        quickFilterValues: value ? [value] : [],
      });
    }
  };

  const handleClearClick = () => {
    setSearchValue('');
    if (apiRef.current?.setQuickFilterValues) {
      apiRef.current.setQuickFilterValues([]);
    } else {
      const fm = apiRef.current?.getFilterModel?.() ?? {};
      apiRef.current?.setFilterModel?.({ ...fm, quickFilterValues: [] });
    }
  };

  return (
    <Toolbar
      className="grid-toolbar d-flex align-items-center justify-content-between"
      sx={{ p: 0.75, pb: 0.35, backgroundColor: '#F7F4F8', borderBottom: 0 }}
    >
      <GridToolbarColumnsButton slotProps={{ button: { startIcon: <ViewColumnRoundedIcon /> } }} />
      
      <TextField
        value={searchValue}
        onChange={handleSearchChange}
        placeholder="Search..."
        variant="standard"
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 1.5 }}>
              <SearchIcon sx={{ fontSize: '1.25rem', color: 'black' }} />
            </InputAdornment>
          ),
          endAdornment: searchValue && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClearClick}
                sx={{ p: 0, '&:hover': { color: 'black' } }}
                aria-label="clear search"
              >
                <ClearIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </InputAdornment>
          ),
          sx: { paddingTop: '4px', paddingBottom: '4px', fontSize: '0.875rem' },
        }}
        sx={{ ml: 'auto', minWidth: 210 }}
      />
    </Toolbar>
  );
}

const ListView = ({ isDataLoaded, events, releaseId, onClose }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [visibleEvents, setVisibleEvents] = useState([]);
    const [hasFetchedRelease, setHasFetchedRelease] = useState(false);
    const apiRef = useGridApiRef();

    useEffect(() => {
        if (releaseId && !hasFetchedRelease && window.location.pathname !== "/") {
            const event = isDataLoaded ? events.find(e => e.release_id === releaseId) : null;
            if (event) {
                setSelectedEvent(event);
                setIsModalOpen(true);
            } else {
                getReleaseDetailsById(releaseId)
                    .then(({ data }) => {
                        if (data?.release_id && data?.vendor_service) {
                            setSelectedEvent(data);
                            setIsModalOpen(true);
                        }
                    })
                    .catch(error => console.error("Error fetching event details:", error));
            }
            setHasFetchedRelease(true);
        }
    }, [releaseId, isDataLoaded, hasFetchedRelease]);     
    
    useEffect(() => {
        handleChange({}, 0);
    }, [events]);

    const toggleDrawer = (event, bool) => {
        setSelectedEvent(event);
        setIsModalOpen(bool);
    };

    const FavoriteButton = () => {
        return (
            < IconButton type="button" aria-label="favorite">
                <StarBorder sx={{ fontSize: '20px', color: '#ddd' }} />
            </IconButton>
        );
    };

    const Relevance = (params) => {
        if (params.value)
            return (
                <div className='d-flex align-items-center'>
                    <StyledRating
                        name="customized-color"
                        defaultValue={params.value ? (params.value === 'low' ? 1 : (params.value === 'medium' ? 2 : 3)) : 0}
                        disabled
                        max={3}
                        icon={<Square sx={{ fontSize: '16px' }} />}
                        emptyIcon={<Square sx={{ fontSize: '16px' }} />}
                    /> <span className='text-s ml-2 text-capitalize'>{params.value}</span>
                </div>
            );
        else return '-';
    };

    const Status = (params) => {
        return (
            <span className={params.value.toUpperCase() === 'COMPLETED' ? 'text-success' : (params.value.toUpperCase() === 'PLANNED' ? 'text-secondary' : 'text-info')}>
                {params.value}
            </span>
        );
    };

    const Service = (params) => {
        return (
            <div><small className='badge font-weight-normal' title={params.value} style={{ backgroundColor: params.row.color['hex'], color: params.row.color['isDark'] ? '#fff' : '#000' }}> {params.value}</small></div>
        );
    };

    const Title = (params) => {
        return (
            <a className='cursor-pointer text-primary text-truncate' title={params.value} onClick={() => toggleDrawer(params.row, true)}> {params.value}</a>
        );
    };

    const handleChange = (event, newValue) => {
        setTabValue(newValue);
        let list = [];
        const today = moment().startOf('day');
        if (newValue === 0) {
            list = events.filter(e => moment(e.planned_end_date).startOf('day').isSameOrAfter(today));
        } else if (newValue === 1) {
            list = events.filter(e => {
                // include invalid dates also to archive
                return !moment(e.planned_end_date, moment.ISO_8601, true).isValid() || moment(e.planned_end_date).startOf('day').isBefore(today);
            });
        }
        setVisibleEvents(list);
    };

    const columns = [
        {
            field: 'release_id',
            headerName: 'ID',
            width: 110,
        },
        {
            field: 'title',
            headerName: 'Title',
            width: 400,
            renderCell: (params) => <Title {...params} />,
        },
        // {
        //     field: '',
        //     headerName: 'Favorite',
        //     width: 120,
        //     renderCell: (params) => <FavoriteButton />,
        // },
        {
            field: 'vendor_service',
            headerName: 'Service',
            width: 120,
            renderCell: (params) => <Service {...params} />,
        },
        {
        field: 'planned_start_date',
        headerName: 'Start Date',
        width: 120,
        renderCell: (params) => {
            const date = params.row?.planned_start_date;
            return date && moment(date, moment.ISO_8601, true).isValid()
            ? moment(date).format('MMM DD YYYY')
            : 'NA';
        },
        },
        {
        field: 'planned_end_date',
        headerName: 'End Date',
        width: 120,
        renderCell: (params) => {
            const date = params.row?.planned_end_date;
            return date && moment(date, moment.ISO_8601, true).isValid()
            ? moment(date).format('MMM DD YYYY')
            : 'NA';
        },
        },
        {
        field: 'last_updated',
        headerName: 'Last Updated',
        width: 120,
        renderCell: (params) => {
            const date = params.row?.last_updated;
            return date && moment(date, moment.ISO_8601, true).isValid()
            ? moment(date).format('MMM DD YYYY')
            : 'NA';
        },
        },
        {
        field: 'release_category',
        headerName: 'Category',
        width: 130,
        renderCell: (params) => `${params.row?.release_category || 'N/A'}`,
        },
        {
            field: 'environment',
            headerName: 'Environemnt',
        },
        {
        field: 'relevance',
        headerName: 'Impact',
        width: 150,
        renderCell: (params) => <Relevance {...params} />,
        },

        {
            field: 'release_status',
            headerName: 'Status for your org',
            width: 150,
            renderCell: (params) => <Status {...params} />,
        },
        {
        field: 'platform',
        headerName: 'Platform',
        width: 150,
        renderCell: (params) => `${params.row?.platform || 'N/A'}`,
        },
        {
        field: 'impacted_services',
        headerName: 'Impacted Service',
        width: 200,
        renderCell: (params) => `${params.row?.impacted_services || 'N/A'}`,
        },

        {
        field: 'act_by',
        headerName: 'Act By',
        width: 100,
        renderCell: (params) => `${params.row?.act_by || 'N/A'}`,
        },

        {
        field: 'geo',
        headerName: 'Geo',
        renderCell: (params) => `${params.row?.geo || 'N/A'}`,
        },

        {
        field: 'what_action_needed',
        headerName: 'What Action Needed',
        width: 200,
        renderCell: (params) => `${params.row?.what_action_needed || 'N/A'}`,
        }
    ];

    return (
        <div className=''>
            <Box className="mb-1 mt-4" sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tabValue} onChange={handleChange} aria-label="basic tabs example">
                    <Tab label={
                        <React.Fragment>
                            <span className='d-flex'>Inbox <span style={{ fontSize: "smaller" }} className='ml-1'>({tabValue === 0 ? visibleEvents.length : events.length - visibleEvents.length})</span></span>
                        </React.Fragment>}
                        {...a11yProps(0)} />
                    <Tab label={
                        <React.Fragment>
                            <span className='d-flex'>Archive <span style={{ fontSize: "smaller" }} className='ml-1'>({tabValue === 1 ? visibleEvents.length : events.length - visibleEvents.length})</span></span>
                        </React.Fragment>}
                        {...a11yProps(1)} />
                </Tabs>
            </Box>
            <div className="mt-1">
                {
                    events.length <= 0 ? (
                        <Alert severity="warning" className=''>
                            No events found.
                        </Alert>
                    ) : (
                        <Box sx={{ height: '100%', overflow: 'auto' }}>
                            <StripedDataGrid
                                apiRef={apiRef} 
                                sx={{
                                    borderColor: 'rgba(224, 224, 224, 1)',
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-main': { margin: 0 }, 
                                }}
                                showToolbar
                                rows={visibleEvents}
                                columns={columns}
                                getRowClassName={(params) =>
                                    params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                }
                                getRowId={(row) => row.release_id}
                                rowHeight={32}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 15,
                                        },
                                    },
                                    columns: {
                                        columnVisibilityModel: {
                                            impacted_services: false,
                                            platform: false,
                                            act_by: false,
                                            last_updated: false,
                                            geo: false,
                                        }
                                    },
                                }}
                                slots={{
                                    toolbar: () => <QuickSearchToolbar apiRef={apiRef} />,
                                    noResultsOverlay: () => (
                                        <Stack height="100%" alignItems="center" justifyContent="center">
                                            No rows to show.
                                        </Stack>
                                    ),
                                }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                    },
                                }}
                                pageSizeOptions={[15, 30, 50, 100]}
                                disableRowSelectionOnClick
                                disableColumnFilter
                                disableDensitySelector
                            />
                        </Box>
                    )
                }
                {
                    selectedEvent && (
                        <EventDetails event={selectedEvent} isOpen={isModalOpen} setIsModalOpen={toggleDrawer} onClose={onClose}/>
                    )
                }
            </div>
        </div>
    );
};

export default ListView;
